import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useRecoilState } from 'recoil';
import { endPoints } from 'api/endpoints';
import axios from 'api/request';
import { useMutation } from 'react-query';
import PropTypes from 'prop-types';
import Select from 'pages/reference-implementation/components/select';
import Btn from 'components/button';
import { RIStyles } from 'pages/reference-implementation/Style';
import { commonStyles as style } from 'assets/style';
import { isEksAdGroupValid } from 'pages/reference-implementation/provisioning/RI003AWS/store';
import Cluster from './components/Cluster';
import EksAdGroups from './components/EksAdGroups';

const Eksns = (props) => {
    const { resource, handleDrawerAction, order } = props;
    const [options, setOptions] = React.useState([]);
    const [departmentOptions, setDepartmentOptions] = React.useState([]);

    const [formData, setFormData] = useState({
        eksDepartment: resource?.configureData?.eksDepartment || null,
        program: resource?.configureData?.program || null,
        cluster: resource?.configureData?.cluster || null,
        eksAdGroup: resource?.configureData?.eksAdGroup || null,
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [adGroupValid] = useRecoilState(isEksAdGroupValid);

    const departmentApi = useMutation((sector) => axios.get(endPoints.aws.lookups.eksSector(sector)));

    const validateForm = () => {
        const newErrors = {};

        if (!formData.eksDepartment) {
            newErrors.eksDepartment = { type: 'required', message: 'Department is required' };
        }
        if (!formData.program) {
            newErrors.program = { type: 'required', message: 'Program is required' };
        }
        if (!formData.cluster) {
            newErrors.cluster = { type: 'required', message: 'Cluster is required' };
        }
        if (!formData.eksAdGroup || !adGroupValid) {
            newErrors.eksAdGroup = { type: 'required', message: 'AD Group is required' };
        }

        setErrors(newErrors);
        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    };

    const handleOnSelectChange = (key, value) => {
        setFormData({
            ...formData,
            [key]: value,
        });
        setTouched({
            ...touched,
            [key]: true,
        });
    };

    const handleSubmit = () => {
        // Set all fields as touched to trigger validation errors display
        setTouched({
            eksDepartment: true,
            program: true,
            cluster: true,
            eksAdGroup: true,
        });
        const checkFormValid = validateForm();
        handleDrawerAction('save', formData, checkFormValid);
    };

    const shouldShowError = (field) => {
        return touched[field] && !!errors[field];
    };

    const saveButton = () => (
        <Btn className="custom-btn" variant="contained" sx={style.modalSaveButton} onClick={() => handleSubmit()} data-testid="save-btn">
            Save
        </Btn>
    );
    const cancelButton = () => (
        <Btn
            className="custom-btn"
            // sx={RIStyles.tags.resetBtn}
            variant="outlined"
            sx={style.modalCancelButton}
            onClick={() => handleDrawerAction('cancel')}
            data-testid="cancel-btn"
        >
            Cancel
        </Btn>
    );

    const programApi = useMutation((payload) => axios.post(`${endPoints.riServices.COMMON.programEks}`, payload));

    const resetOptions = () => {
        setOptions([]);
    };

    React.useEffect(() => {
        if (programApi?.isSuccess) {
            const optionData =
                programApi?.data?.messages?.[0] === 'No Data Found'
                    ? [{ label: 'Not Applicable', value: 'Not Applicable' }]
                    : programApi?.data?.data?.map((app) => ({ label: app, value: app }));
            setOptions(optionData);
        }
    }, [programApi?.isSuccess]);

    React.useEffect(() => {
        if (departmentApi?.isSuccess && departmentApi?.data?.data?.departments) {
            const { departments } = departmentApi?.data?.data;
            const hiddenDepartments = ['Shared Consumer App Network', 'OT Network'];
            const deptOptions = departments
                ?.filter((dept) => !hiddenDepartments.includes(dept.departmentDisplayName))
                ?.map((dept) => ({
                    label: dept?.departmentDisplayName,
                    value: dept?.departmentBackendName,
                }));
            setDepartmentOptions(deptOptions);
        }
    }, [departmentApi?.isSuccess, departmentApi?.data]);

    useEffect(() => {
        if (Object.keys(touched).length > 0) {
            validateForm();
        }
    }, [formData]);

    React.useEffect(() => {
        if (departmentOptions && departmentOptions?.length === 1 && !formData?.eksDepartment) {
            setFormData((prev) => ({ ...prev, eksDepartment: departmentOptions[0].value }));
        }
    }, [departmentOptions]);

    React.useEffect(() => {
        if (options && options?.length === 1 && !formData?.program) {
            setFormData((prev) => ({ ...prev, program: options?.[0].value }));
        }
    }, [options, formData]);

    React.useEffect(() => {
        if (order?.sector) {
            departmentApi.mutate(order?.sector);
        } else {
            setDepartmentOptions([]);
        }
    }, [order?.sector]);

    React.useEffect(() => {
        if (!(!order?.sector || !order?.environment || !formData?.eksDepartment || !order?.appRegion)) {
            programApi.mutate({
                sector: order?.sector,
                environment: order?.environment,
                department: formData?.eksDepartment || null,
                applicationRegion: order?.appRegion,
            });
        } else {
            resetOptions();
        }
    }, [order?.sector, order?.environment, formData?.eksDepartment, order?.appRegion]);

    return (
        <Box style={RIStyles.tags.wrapper}>
            <Box sx={RIStyles.tags.contentWrapper}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                key="Department"
                                label="EKS Department *"
                                value={formData?.eksDepartment || null}
                                options={departmentOptions}
                                handleOnSelect={(value) => handleOnSelectChange('eksDepartment', value?.value)}
                                isRequired={shouldShowError('eksDepartment')}
                                isLoading={departmentApi?.isLoading}
                                disabled={departmentApi?.isLoading || !order?.sector}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                key="Program"
                                label="Program"
                                value={formData?.program}
                                options={options}
                                handleOnSelect={(value) => handleOnSelectChange('program', value?.value)}
                                isRequired={shouldShowError('program')}
                                isLoading={programApi.isLoading}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Cluster
                            order={{ ...order, eksDepartment: formData?.eksDepartment, program: formData?.program }}
                            clusterValue={formData?.cluster}
                            handleOnSelect={(value) => handleOnSelectChange('cluster', value)}
                            isRequired={shouldShowError('cluster')}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <EksAdGroups
                            value={formData?.eksAdGroup}
                            onChange={(val) => handleOnSelectChange('eksAdGroup', val)}
                            isRequired={shouldShowError('eksAdGroup')}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box className="footer" sx={RIStyles.tags.actionWrapper} gap={2}>
                {saveButton()}
                {cancelButton()}
            </Box>
        </Box>
    );
};

Eksns.propTypes = {
    resource: PropTypes.any.isRequired,
    handleDrawerAction: PropTypes.func.isRequired,
    order: PropTypes.object.isRequired,
};

export default Eksns;
